import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import {
    Container, Box, Paper, Typography, TableContainer,
    Table, TableHead, TableRow, TableCell, TableBody, Chip,
    Stack, Alert, Button, Skeleton, TextField, InputAdornment,
    Tooltip, ToggleButtonGroup, ToggleButton, Divider
} from '@mui/material';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { alpha } from '@mui/material/styles';
import { User } from 'firebase/auth';
import { CalendarEvent } from '../types';
import { logEvent } from '../analytics';
import { useSeason } from '../SeasonContext';
import { useRoster } from '../RosterContext';
import { CURRENT_SEASON_KEY } from '../seasons';
import { normalizeGoogleSheetEditUrl } from '../utils/sheetUtils';
import { fetchAndMergeCalendarData } from '../utils/dataUtils';
import ScheduleConflictDisplay from './ScheduleConflictDisplay';
import {
    formatLocation,
    formatEventTime,
    formatEventDateRange,
    getLiveBarnLink,
    parseEventDate,
    getEventType,
    EventType,
    isHomeGame,
    isTournamentEvent,
} from '../utils/eventUtils';

interface OutletContextType {
    user: User | null;
    isTeamManager: boolean;
}

export interface UserDutyAssignment {
    event: CalendarEvent;
    duty: string;
    volunteerName: string;
}

const dayDiff = (date1: Date, date2: Date): number => {
    const _MS_PER_DAY = 1000 * 60 * 60 * 24;
    const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
    return Math.floor((utc2 - utc1) / _MS_PER_DAY);
};

const getRelativeTimingText = (eventDate: Date): { text: string; isPast: boolean } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
    const days = dayDiff(today, targetDay);

    if (days < 0) {
        return { text: 'Past', isPast: true };
    }
    if (days === 0) {
        return { text: 'Today', isPast: false };
    }
    if (days === 1) {
        return { text: 'Tomorrow', isPast: false };
    }
    return { text: `in ${days} days`, isPast: false };
};

const DUTY_COLORS: Record<string, 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'default'> = {
    'Scoreboard': 'primary',
    'Gamesheet': 'secondary',
    'Penalty Box': 'warning',
    'Music': 'info',
};

const VolunteerPage: React.FC = () => {
    const { selectedSeason, isViewingPastSeason, setSelectedSeasonKey } = useSeason();
    const { getRoster, isAuthReady, userProfile } = useRoster();
    const outletContext = useOutletContext<OutletContextType | undefined>();
    const user = outletContext?.user || null;
    const isTeamManager = outletContext?.isTeamManager || false;

    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [filterScope, setFilterScope] = useState<'all' | 'upcoming'>('all');

    const userEmail = user?.email?.toLowerCase().trim();
    const currentSeasonRoster = getRoster(selectedSeason.key) || getRoster(CURRENT_SEASON_KEY) || getRoster('pw_26_27');
    const rawVolunteerSheetUrl = selectedSeason?.volunteerSheetUrl;
    const volunteerSheetEditUrl = useMemo(
        () => normalizeGoogleSheetEditUrl(rawVolunteerSheetUrl),
        [rawVolunteerSheetUrl]
    );

    useEffect(() => {
        if (isViewingPastSeason) {
            document.title = `Jets '14 | ${selectedSeason.label}`;
            return;
        }
        document.title = "Jets '14 | Volunteers";
        logEvent('view_volunteers_page');
    }, [isViewingPastSeason, selectedSeason.label]);

    const loadData = useCallback(async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        try {
            const mergedEvents = isRefresh
                ? await fetchAndMergeCalendarData({ forceRefresh: true, seasonKey: selectedSeason.key })
                : await fetchAndMergeCalendarData();
            setEvents(mergedEvents);
            setError(null);
        } catch (err: any) {
            setError(err?.message || 'Failed to load volunteer duties');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [selectedSeason.key]);

    useEffect(() => {
        if (!isAuthReady) return;
        loadData();
    }, [isAuthReady, loadData]);

    // Resolve user's associated family/player last names from roster & profile
    const userLastNames = useMemo<string[]>(() => {
        const lastNames = new Set<string>();
        if (!userEmail) return [];

        if (currentSeasonRoster) {
            for (const player of currentSeasonRoster.players || []) {
                const parentEmails = (player.parents || [])
                    .map(p => p.email?.toLowerCase().trim())
                    .filter(Boolean) as string[];
                if (parentEmails.includes(userEmail)) {
                    if (player.lastName) {
                        lastNames.add(player.lastName.trim().toLowerCase());
                    }
                }
            }

            for (const staffMember of currentSeasonRoster.staff || []) {
                const emails = [staffMember.email, staffMember.secondaryEmail]
                    .map(e => e?.toLowerCase().trim())
                    .filter(Boolean) as string[];
                if (emails.includes(userEmail)) {
                    if (staffMember.lastName) {
                        lastNames.add(staffMember.lastName.trim().toLowerCase());
                    }
                }
            }
        }

        if (userProfile?.associatedPlayers) {
            for (const p of userProfile.associatedPlayers) {
                if (p.lastName) {
                    lastNames.add(p.lastName.trim().toLowerCase());
                }
            }
        }

        return Array.from(lastNames);
    }, [userEmail, currentSeasonRoster, userProfile]);

    const isUserVolunteer = useCallback((volunteerName?: string | null): boolean => {
        if (!volunteerName || typeof volunteerName !== 'string' || userLastNames.length === 0) {
            return false;
        }
        const normalized = volunteerName.toLowerCase().trim();
        return userLastNames.some(lastName => normalized.includes(lastName));
    }, [userLastNames]);

    // Filter events that have volunteer duties defined or are upcoming regular games that require volunteer duties
    const eventsWithDuties = useMemo(() => {
        const now = new Date();
        const thirtyMinAgo = new Date(now.getTime() - 30 * 60 * 1000);

        return events
            .filter(e => {
                const hasDuties = !!e.duties && Object.keys(e.duties).length > 0;
                if (hasDuties) return true;

                // Upcoming regular league games need volunteers (home games need scoreboard/gamesheet, away games need penalty box)
                const eventDate = parseEventDate(e.start);
                const isUpcoming = eventDate >= thirtyMinAgo;
                const type = getEventType(e);
                const isTournament = type === EventType.Tournament || !!e.isTournament || isTournamentEvent(e);
                const isRegularGame = type === EventType.Game && !isTournament;

                return isUpcoming && isRegularGame;
            })
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    }, [events]);

    // Extract all assigned duties for the logged-in user
    const userDuties = useMemo(() => {
        const assignments: UserDutyAssignment[] = [];
        if (userLastNames.length === 0) return assignments;

        for (const event of eventsWithDuties) {
            if (event.duties) {
                for (const [duty, name] of Object.entries(event.duties)) {
                    if (isUserVolunteer(name)) {
                        assignments.push({
                            event,
                            duty,
                            volunteerName: name
                        });
                    }
                }
            }
        }
        return assignments;
    }, [eventsWithDuties, isUserVolunteer, userLastNames]);

    // Filtered full schedule list based on search and upcoming filter
    const filteredEventsWithDuties = useMemo(() => {
        const now = new Date();
        const thirtyMinAgo = new Date(now.getTime() - 30 * 60 * 1000);
        const query = searchQuery.trim().toLowerCase();

        return eventsWithDuties.filter(event => {
            if (filterScope === 'upcoming') {
                const eventEnd = event.end ? new Date(event.end) : new Date(event.start);
                if (eventEnd < thirtyMinAgo) return false;
            }

            if (!query) return true;

            const summaryMatch = event.summary.toLowerCase().includes(query);
            const locationMatch = (event.location || '').toLowerCase().includes(query);
            const dutiesMatch = event.duties && Object.entries(event.duties).some(
                ([duty, name]) => duty.toLowerCase().includes(query) || (typeof name === 'string' && name.toLowerCase().includes(query))
            );
            const isHome = isHomeGame(event);
            const type = getEventType(event);
            const isTournament = type === EventType.Tournament || !!event.isTournament || isTournamentEvent(event);
            const isRegularGame = type === EventType.Game && !isTournament;
            const isPast = getRelativeTimingText(parseEventDate(event.start)).isPast;
            const isScoreboardReq = !isPast && isRegularGame && isHome;
            const isGamesheetReq = !isPast && isRegularGame && isHome;
            const isPenaltyBoxReq = !isPast && isRegularGame && !isHome;
            const hasMissing = (isScoreboardReq && !event.duties?.['Scoreboard']) ||
                               (isGamesheetReq && !event.duties?.['Gamesheet']) ||
                               (isPenaltyBoxReq && !event.duties?.['Penalty Box'] && !event.duties?.['Penalty']);
            const unassignedMatch = ('unassigned'.includes(query) || 'needed'.includes(query) || 'missing'.includes(query)) && hasMissing;

            return summaryMatch || locationMatch || dutiesMatch || unassignedMatch;
        });
    }, [eventsWithDuties, filterScope, searchQuery]);

    // Summary counters
    const upcomingUserDutiesCount = useMemo(() => {
        const now = new Date();
        return userDuties.filter(u => new Date(u.event.start) >= now).length;
    }, [userDuties]);

    if (isViewingPastSeason) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ my: 6 }}>
                    <Paper variant="outlined" elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2, textAlign: 'center' }}>
                        <VolunteerActivismIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
                            Volunteer Duties
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Volunteer duty schedules are active exclusively for the current 26/27 Peewee Major season.
                        </Typography>
                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button
                                variant="contained"
                                onClick={() => setSelectedSeasonKey(CURRENT_SEASON_KEY)}
                            >
                                Switch to 26/27 Season
                            </Button>
                            <Button
                                variant="outlined"
                                component={RouterLink}
                                to={`/standings/${selectedSeason.key}`}
                                startIcon={<LeaderboardIcon />}
                            >
                                View {selectedSeason.label} Standings
                            </Button>
                        </Stack>
                    </Paper>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ my: { xs: 2, sm: 4 } }}>
                {/* Header Title and Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" component="h1" fontWeight="bold">
                            Volunteers
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Chicago Jets 2014 • 26/27 Season Volunteer Schedule
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        {isTeamManager && volunteerSheetEditUrl && (
                            <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                startIcon={<OpenInNewIcon />}
                                href={volunteerSheetEditUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Edit Volunteer Sheet
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                logEvent('refresh_volunteer_duties');
                                loadData(true);
                            }}
                            disabled={loading || refreshing}
                        >
                            {refreshing ? 'Refreshing...' : 'Refresh'}
                        </Button>
                    </Stack>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} action={
                        <Button color="inherit" size="small" onClick={() => loadData()}>Retry</Button>
                    }>
                        {error}
                    </Alert>
                )}

                {isTeamManager && (
                    <ScheduleConflictDisplay
                        calendarEvents={events}
                        isLoadingEvents={loading}
                        filter="volunteers"
                        volunteerSheetUrl={volunteerSheetEditUrl}
                    />
                )}

                {/* Section 1: User's Assigned Volunteer Duties */}
                <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, mb: 4, overflow: 'hidden' }}>
                    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper' }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <AssignmentIndIcon color="primary" sx={{ fontSize: 28 }} />
                                <Typography variant="h6" component="h2" fontWeight="bold">
                                    Your Assigned Duties
                                </Typography>
                            </Stack>
                            {userDuties.length > 0 && (
                                <Chip
                                    label={`${userDuties.length} ${userDuties.length === 1 ? 'Duty' : 'Duties'} Assigned (${upcomingUserDutiesCount} Upcoming)`}
                                    color="primary"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            )}
                        </Stack>
                    </Box>
                    <Divider />

                    {loading ? (
                        <Box sx={{ p: 3 }}>
                            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1, mb: 1.5 }} />
                            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                        </Box>
                    ) : userDuties.length > 0 ? (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table aria-label="your volunteer duties table">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>Date & Time</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '28%' }}>Game / Event</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Location</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Assigned Duty</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold', width: '10%' }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {userDuties.map((item, index) => {
                                        const eventDate = parseEventDate(item.event.start);
                                        const timing = getRelativeTimingText(eventDate);
                                        const isInternalLink = item.event.url?.startsWith('/');
                                        const dutyColor = DUTY_COLORS[item.duty] || 'default';

                                        return (
                                            <TableRow
                                                key={`${item.event.start}-${item.duty}-${index}`}
                                                hover
                                                sx={{
                                                    bgcolor: timing.isPast ? 'action.hover' : 'inherit',
                                                    '&:last-child td, &:last-child th': { border: 0 }
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="600">
                                                        {formatEventDateRange(item.event)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatEventTime(item.event, 'start')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="500">
                                                        {item.event.url ? (
                                                            isInternalLink ? (
                                                                <RouterLink to={item.event.url} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                                                    {item.event.summary}
                                                                </RouterLink>
                                                            ) : (
                                                                <a href={item.event.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                                                    {item.event.summary}
                                                                </a>
                                                            )
                                                        ) : (
                                                            item.event.summary
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" component="div">
                                                        {formatLocation(item.event.location, item.event)}
                                                    </Typography>
                                                    {getLiveBarnLink(item.event.location, item.event.start)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={item.duty}
                                                        color={dutyColor}
                                                        size="small"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={timing.text}
                                                        size="small"
                                                        variant={timing.isPast ? 'outlined' : 'filled'}
                                                        color={timing.isPast ? 'default' : (timing.text === 'Today' ? 'error' : (timing.text === 'Tomorrow' ? 'warning' : 'info'))}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                            <CheckCircleOutlineIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
                            <Typography variant="h6" color="text.primary" gutterBottom>
                                You are not signed up to volunteer
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                                {userLastNames.length > 0
                                    ? `No volunteer duties are currently assigned to the ${userLastNames.map(n => n.charAt(0).toUpperCase() + n.slice(1)).join(' / ')} family in the schedule.`
                                    : 'No volunteer duties were found for your signed-in account. You can view the full schedule below to see all team assignments.'}
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* Section 2: Complete Volunteer Schedule (All Events) */}
                <Paper variant="outlined" elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <CalendarMonthIcon color="primary" sx={{ fontSize: 28 }} />
                                <Box>
                                    <Typography variant="h6" component="h2" fontWeight="bold">
                                        All Volunteer Duties
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Showing {filteredEventsWithDuties.length} of {eventsWithDuties.length} scheduled games with duties
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" gap={1}>
                                <ToggleButtonGroup
                                    size="small"
                                    value={filterScope}
                                    exclusive
                                    onChange={(_, val) => {
                                        if (val) setFilterScope(val);
                                    }}
                                    aria-label="duty schedule filter"
                                >
                                    <ToggleButton value="all">All Games</ToggleButton>
                                    <ToggleButton value="upcoming">Upcoming</ToggleButton>
                                </ToggleButtonGroup>

                                <TextField
                                    size="small"
                                    placeholder="Search by family, opponent, rink..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ minWidth: { xs: '100%', sm: 260 } }}
                                />
                            </Stack>
                        </Box>
                    </Box>
                    <Divider />

                    {loading ? (
                        <Box sx={{ p: 3 }}>
                            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 1 }} />
                            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 1 }} />
                            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 1 }} />
                            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
                        </Box>
                    ) : filteredEventsWithDuties.length > 0 ? (
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table aria-label="complete volunteer schedule table" size="medium">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>Date & Time</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Game / Event</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Location</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Scoreboard</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Gamesheet</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Penalty Box</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Music</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredEventsWithDuties.map((event, idx) => {
                                        const eventDate = parseEventDate(event.start);
                                        const timing = getRelativeTimingText(eventDate);
                                        const isUpcoming = !timing.isPast;
                                        const isInternalLink = event.url?.startsWith('/');
                                        const duties = event.duties || {};

                                        const scoreboard = duties['Scoreboard'];
                                        const gamesheet = duties['Gamesheet'];
                                        const penaltyBox = duties['Penalty Box'] || duties['Penalty'];
                                        const music = duties['Music'];

                                        const eventType = getEventType(event);
                                        const isTournament = eventType === EventType.Tournament || !!event.isTournament || isTournamentEvent(event);
                                        const isHome = isHomeGame(event);
                                        const isRegularGame = eventType === EventType.Game && !isTournament;

                                        // Home games require Scoreboard & Gamesheet (no penalty box). Away games require Penalty Box (no scoreboard/gamesheet). Tournament games require none.
                                        const isScoreboardRequired = isUpcoming && isRegularGame && isHome;
                                        const isGamesheetRequired = isUpcoming && isRegularGame && isHome;
                                        const isPenaltyBoxRequired = isUpcoming && isRegularGame && !isHome;

                                        const isMissingRequiredDuties = (isScoreboardRequired && !scoreboard) ||
                                                                        (isGamesheetRequired && !gamesheet) ||
                                                                        (isPenaltyBoxRequired && !penaltyBox);

                                        const renderVolunteerCell = (name?: string, dutyType?: string) => {
                                            if (!name) {
                                                const isNeeded =
                                                    (dutyType === 'Scoreboard' && isScoreboardRequired) ||
                                                    (dutyType === 'Gamesheet' && isGamesheetRequired) ||
                                                    (dutyType === 'Penalty Box' && isPenaltyBoxRequired);

                                                if (isNeeded) {
                                                    return (
                                                        <Tooltip title="Volunteer duty is unassigned for this upcoming game" arrow>
                                                            <Chip
                                                                label="Needed"
                                                                size="small"
                                                                color="warning"
                                                                variant="outlined"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    fontSize: '0.75rem',
                                                                    borderStyle: 'dashed',
                                                                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    );
                                                }
                                                return <Typography variant="body2" color="text.disabled">—</Typography>;
                                            }
                                            const isMine = isUserVolunteer(name);
                                            if (isMine) {
                                                return (
                                                    <Tooltip title="Assigned to your family" arrow>
                                                        <Chip
                                                            label={name}
                                                            color="primary"
                                                            size="small"
                                                            sx={{ fontWeight: 'bold' }}
                                                        />
                                                    </Tooltip>
                                                );
                                            }
                                            return (
                                                <Typography variant="body2" fontWeight="500">
                                                    {name}
                                                </Typography>
                                            );
                                        };

                                        return (
                                            <TableRow
                                                key={`${event.start}-${idx}`}
                                                hover
                                                sx={{
                                                    bgcolor: timing.isPast
                                                        ? 'action.hover'
                                                        : (isMissingRequiredDuties
                                                            ? (theme) => alpha(theme.palette.warning.main, 0.04)
                                                            : 'inherit'),
                                                    '&:last-child td, &:last-child th': { border: 0 }
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="600">
                                                        {formatEventDateRange(event)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatEventTime(event, 'start')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                                                        <Typography variant="body2" fontWeight="500">
                                                            {event.url ? (
                                                                isInternalLink ? (
                                                                    <RouterLink to={itemUrl(event.url)} style={{ color: 'inherit', textDecoration: 'underline' }}>
                                                                        {event.summary}
                                                                    </RouterLink>
                                                                ) : (
                                                                    <a href={event.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                                                                        {event.summary}
                                                                    </a>
                                                                )
                                                            ) : (
                                                                event.summary
                                                            )}
                                                        </Typography>
                                                        {isMissingRequiredDuties && (
                                                            <Chip
                                                                label="Needs Volunteers"
                                                                size="small"
                                                                color="warning"
                                                                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 'bold' }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" component="div">
                                                        {formatLocation(event.location, event)}
                                                    </Typography>
                                                    {getLiveBarnLink(event.location, event.start)}
                                                </TableCell>
                                                <TableCell>{renderVolunteerCell(scoreboard, 'Scoreboard')}</TableCell>
                                                <TableCell>{renderVolunteerCell(gamesheet, 'Gamesheet')}</TableCell>
                                                <TableCell>{renderVolunteerCell(penaltyBox, 'Penalty Box')}</TableCell>
                                                <TableCell>{renderVolunteerCell(music, 'Music')}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                            <InfoOutlinedIcon color="action" sx={{ fontSize: 36, mb: 1 }} />
                            <Typography variant="body1" color="text.secondary">
                                {searchQuery ? `No volunteer duties matching "${searchQuery}".` : 'No volunteer duties scheduled for this filter.'}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

// Helper for internal links
const itemUrl = (url: string) => url;

export default VolunteerPage;
